import { ReviewRefreshRequiredError } from "components/manualReview/utils/reviewErrors";
import { useVerifyPeriods } from "components/manualReview/hooks/useVerifyPeriods";
import {
  dedupeReviewEvents,
  stableReviewEventId,
} from "components/manualReview/utils/eventUtils";
import { fromReviewEvent } from "components/manualReview/contexts/ManualReviewEventContext";
import { useAreaLabelledEvents } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useActivityTimer } from "hooks/useActivityTimer";
import { useTenant } from "hooks/settings";
import { useCallback } from "react";
import { useScrapEyeMetadata } from "src/contexts/scrapEyeMetadata";
import {
  CommentedEvent,
  useGetEventsForReviewQuery,
  useGetReviewDayOverviewQuery,
} from "src/store/api/generatedApi";

dayjs.extend(utc);

/**
 * Resolve a commented event by reverifying its whole review period with fresh
 * events. The period's sibling events are resubmitted unchanged; the commented
 * event is resubmitted with the reviewer's edits (its comment carried forward).
 */
export const useReverifyCommentedPeriod = ({
  commentedEvent,
  location,
  enabled,
}: {
  commentedEvent: CommentedEvent;
  location: string;
  enabled: boolean;
}) => {
  const tenantName = useTenant();
  const { default_areas: defaultAreas } = useScrapEyeMetadata();
  const withAreaLabels = useAreaLabelledEvents(location);
  const {
    getElapsedSeconds,
    reset: resetTimer,
    sessionStartedAt,
  } = useActivityTimer();

  const { event } = commentedEvent;

  const overviewQuery = useGetReviewDayOverviewQuery(
    {
      tenantName,
      location,
      date: dayjs.utc(event.data.start).format("YYYY-MM-DD"),
    },
    { skip: !enabled }
  );

  const period = overviewQuery.data?.review_periods.find((candidate) => {
    const start = dayjs.utc(event.data.start);
    return (
      !start.isBefore(dayjs.utc(candidate.start)) &&
      start.isBefore(dayjs.utc(candidate.end))
    );
  });

  const eventsQuery = useGetEventsForReviewQuery(
    {
      tenantName,
      location,
      windowStart: period?.start ?? "",
      windowEnd: period?.end ?? "",
      includeVerified: true,
    },
    { skip: !enabled || period === undefined }
  );

  const { verifyPeriods, isVerifying } = useVerifyPeriods({
    tenantName,
    location,
    getElapsedSeconds,
    resetTimer,
    sessionStartedAt,
    reviewSlotRevisions: eventsQuery.data?.review_slot_revisions,
  });

  const reverify = useCallback(
    async (edits: Partial<ScrapEyesEvent>) => {
      if (period === undefined || eventsQuery.data === undefined) {
        throw new Error("Review period not loaded");
      }

      const periodStart = dayjs.utc(period.start);
      const periodEnd = dayjs.utc(period.end);
      const periodEvents = withAreaLabels(
        dedupeReviewEvents(eventsQuery.data.review_events)
          .filter((reviewEvent) => {
            const start = dayjs.utc(reviewEvent.merged.event.start);
            return !start.isBefore(periodStart) && start.isBefore(periodEnd);
          })
          .map((reviewEvent) => fromReviewEvent(reviewEvent, defaultAreas))
      );

      const targetId = stableReviewEventId({
        merged: {
          event: event.data,
          labels: event.labels.map((label) => label.data),
        },
        original_events: [],
        mass_event: null,
        mass_outlier_band: null,
        review_reasons: [],
      });

      if (!periodEvents.some((periodEvent) => periodEvent.id === targetId)) {
        throw new ReviewRefreshRequiredError(
          "Commented event not found in its review period"
        );
      }

      const editedEvents = periodEvents.map((periodEvent) =>
        periodEvent.id === targetId
          ? { ...periodEvent, ...edits, id: periodEvent.id }
          : periodEvent
      );

      await verifyPeriods([period], editedEvents, periodEvents, {
        includeVerified: true,
      });
    },
    [
      period,
      eventsQuery.data,
      defaultAreas,
      event,
      verifyPeriods,
      withAreaLabels,
    ]
  );

  return {
    reverify,
    isReverifying: isVerifying,
    isLoading: period === undefined || eventsQuery.data === undefined,
  };
};
